"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const config_1 = require("config");
const UrlHelper_1 = require("../helpers/UrlHelper");
const Environment_1 = require("../models/Environment");
const FeaturesService_1 = require("../services/FeaturesService");
const osRegExps = {};
osRegExps[Environment_1.OperatingSystems.WinPhone] = /windows\sphone/i;
osRegExps[Environment_1.OperatingSystems.WinRT] = /arm;/i;
osRegExps[Environment_1.OperatingSystems.Win] = /(windows|win32)/i;
osRegExps[Environment_1.OperatingSystems.iOS] = /(iPad|iPhone|iPod)/i;
osRegExps[Environment_1.OperatingSystems.MacOS] = /(macintosh|mac os x)/i;
osRegExps[Environment_1.OperatingSystems.Android] = /android/i;
osRegExps[Environment_1.OperatingSystems.ChromeOS] = /CrOS/i;
osRegExps[Environment_1.OperatingSystems.Linux] = /(linux|x11)/i;
const mobileRegExp = /(android|ipod|windows phone|wpdesktop|windows ce|blackberry\w*|meego|webos|palm|symbian|pda|\w*?mobile\w*?|\w*?phone\w*?)/i;
const tabletRegExp = /tablet|ipad/i;
const touchIERegExp = /^(?=.*\bTrident\b)(?=.*\bTablet PC\b).*$/i;
const ieVersionRegExp = /(?:MSIE |Trident\/.*;\s+rv:)(\d+)/ig;
const windows10RegExp = /Windows NT 10/i;
const windows81RegExp = /Windows NT 6\.3/i;
const windows8RegExp = /Windows NT 6\.2/i;
const windows7RegExp = /(Windows NT 6\.1)|(WOW64)/i;
const isEdgeAnaheim = /Edg\//i; // should be used only for showing different visuals, not for feature set distinguishing as it behaves as Chrome
const macIntelPlatformRegexp = /(macIntel|iPad|iPhone)/i;
const macRegexp = /(macintosh|mac os x)/i;
const iPADRendererWebGL = /Apple/i; // can be "Apple A12X GPU" or "Apple GPU" depending on iOS version.
const iPADVendorWebGL = "Apple Inc.";
class EnvironmentService {
    constructor() {
        this.getUserAgent = () => {
            return window.navigator.userAgent;
        };
        this.getOS = (userAgent = window.navigator.userAgent) => {
            let osName;
            for (osName in Environment_1.OperatingSystems) {
                if (Object.prototype.hasOwnProperty.call(Environment_1.OperatingSystems, osName)) {
                    const regexp = osRegExps[Environment_1.OperatingSystems[osName]];
                    if (regexp && userAgent.match(regexp)) {
                        if (Environment_1.OperatingSystems[osName] === Environment_1.OperatingSystems.MacOS) {
                            if (this.isDeviceIPad()) {
                                return Environment_1.OperatingSystems[Environment_1.OperatingSystems.iOS];
                            }
                        }
                        return Environment_1.OperatingSystems[osName];
                    }
                }
            }
            return Environment_1.OperatingSystems.Unknown;
        };
        this.getCanonicalOS = (userAgent = window.navigator.userAgent) => {
            const os = this.getOS(userAgent);
            switch (os) {
                case Environment_1.OperatingSystems.Win:
                    return Environment_1.CanonicalOperatingSystems.Win;
                case Environment_1.OperatingSystems.MacOS:
                    return Environment_1.CanonicalOperatingSystems.Mac;
                case Environment_1.OperatingSystems.Linux:
                    return Environment_1.CanonicalOperatingSystems.Linux;
                default:
                    return Environment_1.CanonicalOperatingSystems.Other;
            }
        };
        this.getDeviceType = (userAgent = window.navigator.userAgent) => {
            if (userAgent.match(tabletRegExp) && !userAgent.match(touchIERegExp)) {
                return Environment_1.DeviceTypes.Tablet;
            }
            if (userAgent.match(mobileRegExp)) {
                return Environment_1.DeviceTypes.Mobile;
            }
            if (userAgent.match(macRegexp) && this.isDeviceIPad()) {
                return Environment_1.DeviceTypes.Tablet;
            }
            return Environment_1.DeviceTypes.Desktop;
        };
        this.getBrowserName = (userAgent = window.navigator.userAgent) => {
            let browserName;
            for (browserName in Environment_1.Browsers) {
                if (userAgent.match(new RegExp(Environment_1.Browsers[browserName]))) {
                    return Environment_1.Browsers[browserName];
                }
            }
            return Environment_1.Browsers.Unknown;
        };
        this.getIEVersion = (userAgent = window.navigator.userAgent) => {
            if (this.getBrowserName() !== Environment_1.Browsers.MSIE) {
                throw new Error("Unsupported browser");
            }
            const match = userAgent.match(ieVersionRegExp);
            const versions = (match || [""])
                .map((m) => { const subs = m.split(":"); return subs[subs.length - 1]; })
                .map((n) => { const subs = n.split(" "); return subs[subs.length - 1]; })
                .map((o) => parseInt(o, 10))
                .reduce((acc, cur) => { if (!isNaN(cur)) {
                acc.push(cur);
            } return acc; }, []);
            const version = Math.max(...versions);
            return version ? version : 0;
        };
        // TODO: I believe we could have in config a deployment type, not having in ConfigBase a mapping of domains and types.
        // that doesn't sound right.
        this.getDeploymentType = () => {
            const hostDomain = UrlHelper_1.UrlHelper.getDomain();
            if (!hostDomain) {
                return Environment_1.DeploymentTypes.Other;
            }
            if (hostDomain === config_1.default.urls.domains.devspaces ||
                hostDomain === config_1.default.urls.domains.devLocal) {
                return Environment_1.DeploymentTypes.Devspaces;
            }
            if (hostDomain === config_1.default.urls.domains.life) {
                return Environment_1.DeploymentTypes.Life;
            }
            return Environment_1.DeploymentTypes.Production;
        };
        this.isLWJSupportedBrowser = () => {
            const browserName = this.getBrowserName();
            return (!this.isWebClientSupportedBrowser() &&
                (browserName === Environment_1.Browsers.Firefox
                    || (browserName === Environment_1.Browsers.Safari && this.getOS() !== Environment_1.OperatingSystems.iOS && !this.isSafariVersionFullySupported())
                    || (browserName === Environment_1.Browsers.MSIE && this.getIEVersion() >= 11 && !this.flags.disableContinueButtonOnMSIE)));
        };
        this.isWindows10 = (userAgent = window.navigator.userAgent) => {
            return windows10RegExp.test(userAgent);
        };
        this.isEdgeAnaheim = (userAgent = window.navigator.userAgent) => {
            return isEdgeAnaheim.test(userAgent);
        };
        this.isArchitecturex64 = (userAgent = window.navigator.userAgent) => {
            return (userAgent.indexOf("Win64") !== -1 ||
                userAgent.indexOf("x64") !== -1 ||
                userAgent.indexOf("WOW64") !== -1 ||
                userAgent.indexOf("x86_64") !== -1);
        };
        this.isNativeClientSupportedOS = (userAgent = window.navigator.userAgent) => {
            let supported = !config_1.default.nativeClientUnsupportedOS.some((os) => os === this.getOS(userAgent));
            if (this.getCanonicalOS() === Environment_1.CanonicalOperatingSystems.Win && !(windows10RegExp.test(userAgent) ||
                windows8RegExp.test(userAgent) ||
                windows7RegExp.test(userAgent) ||
                windows81RegExp.test(userAgent))) {
                supported = false;
            }
            return supported;
        };
        this.isWebClientSupportedBrowser = (userAgent = window.navigator.userAgent) => {
            const browserName = this.getBrowserName(userAgent);
            let supported = config_1.default.webClientSupportedBrowsers.some((browser) => browser === browserName);
            // refinement of Safari support
            if (browserName === Environment_1.Browsers.Safari) {
                supported = this.isSafariVersionFullySupported();
            }
            return supported;
        };
        this.getLauncherVersion = () => {
            const v2Prefix = "LauncherV2";
            if (typeof LAUNCHER_VERSION === "string" && LAUNCHER_VERSION) {
                return `${v2Prefix}-${LAUNCHER_VERSION}`;
            }
            return v2Prefix;
        };
        /*
        * Function that returns a true if the UNMASKED_RENDERER_WEBGL and UNMASKED_VENDOR_WEBGL
        * is from Apple.
        */
        this.isWebGLVendorAndRendererApple = () => {
            const canvas = document.createElement("canvas");
            const context = canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
            if (context && "getExtension" in context) {
                const info = context.getExtension("WEBGL_debug_renderer_info");
                if (info && "getParameter" in context) {
                    if ((context.getParameter(info.UNMASKED_RENDERER_WEBGL)).match(iPADRendererWebGL) &&
                        context.getParameter(info.UNMASKED_VENDOR_WEBGL) === iPADVendorWebGL) {
                        return true;
                    }
                }
            }
            return false;
        };
        /*
        * As of iOS 13, safari on iPAD reports the same user-agent as Safari on desktop MacOS.
        * We try to figure out the platform and touch-screen-points to check if it is a
        * 1)apple device - macIntel platform & 2)touch screen & 3) has Apple GPU ? then it MUST be an iPAD.
        */
        this.isDeviceIPad = () => {
            if (this.getPlatform().match(macIntelPlatformRegexp) &&
                this.getMaxTouchPoints() && this.getMaxTouchPoints() > 1 &&
                this.isWebGLVendorAndRendererApple()) {
                return true;
            }
            return false;
        };
        this.getEnvironmentInfo = () => {
            return {
                userAgent: this.getUserAgent(),
                os: this.getOS(),
                canonicalOs: this.getCanonicalOS(),
                deviceType: this.getDeviceType(),
                browserName: this.getBrowserName(),
                deploymentType: this.getDeploymentType(),
                architectureType: this.isArchitecturex64()
                    ? Environment_1.ArchitectureTypes.x64
                    : Environment_1.ArchitectureTypes.Other,
                isWebClientSupportedBrowser: this.isWebClientSupportedBrowser(),
                isNativeClientSupportedOS: this.isNativeClientSupportedOS(),
                isLWJSupportedBrowser: this.isLWJSupportedBrowser(),
                isWindows10: this.isWindows10(),
                isEdgeAnaheim: this.isEdgeAnaheim(),
                isTflDomain: this.isTflDomain(),
            };
        };
        this.getPlatform = () => {
            return window.navigator.platform;
        };
        this.getMaxTouchPoints = () => {
            return window.navigator.maxTouchPoints;
        };
        this.isSafariVersionFullySupported = () => {
            // version = ["Version/14.0", "14", "0"];
            const version = navigator.appVersion.match(/Version\/([0-9]+)\.([0-9]+)/);
            if (version && version.length > 1) {
                const majorVerson = parseInt(version[1], 10);
                const minorVerson = parseInt(version[2], 10);
                if (majorVerson < 13 || majorVerson === 13 && minorVerson === 0) {
                    return false;
                }
            }
            else {
                return false;
            }
            return true;
        };
        this.isTflDomain = () => {
            return this.getDeploymentType() === Environment_1.DeploymentTypes.Life;
        };
    }
    get flags() {
        return FeaturesService_1.default.getFeatureFlags();
    }
    getScreenResolution(screen = window.screen) {
        if (screen.width == null ||
            typeof screen.width === "undefined" ||
            screen.height == null ||
            typeof screen.height === "undefined") {
            return "Unknown";
        }
        return screen.width + "x" + screen.height;
    }
}
const environmentService = new EnvironmentService();
exports.default = environmentService;
